import { random } from 'lodash';
import { ILegalTransactionRelationshipMember } from './ILegalTransactionRelationshipMember';
import { LegalTransactionRelationshipTypeEnum } from './LegalTransactionRelationshipTypeEnum';
import {
  getEmptyFamilyRelationship,
  IFamilyRelationship,
} from './Relationships/Family/ILegalTransactionFamilyRelationship';
import {
  getEmptyGeneralRelationship,
  IGeneralRelationship,
} from './Relationships/General/ILegalTransactionRelationshipGeneral';
import {
  getEmptyInheritanceProvisionRelationship,
  IInheritanceProvisionRelationship,
} from './Relationships/InheritanceProvision/ILegalTransactionRelationshipInheritanceProvision';
import {
  getEmptyLandRegisterQueryRelationship,
  ILandRegisterQueryRelationship,
} from './Relationships/LandRegisterQuery/ILegalTransactionRelationshipLandRegisterQuery';
import {
  getEmptyMarriageRelationship,
  IMarriageRelationship,
} from './Relationships/Marriage/ILegalTransactionRelationshipMarriage';
import {
  getEmptySignatureCertificationRelationship,
  ISignatureCertificationRelationship,
} from './Relationships/SignatureCertification/ILegalTransactionRelationshipSignatureCertification';
import {
  getEmptyTestamentRelationship,
  ITestamentRelationship,
} from './Relationships/Testament/ILegalTransactionRelationshipTestament';

export type RelationshipByType<T extends LegalTransactionRelationshipTypeEnum> =
  Extract<ILegalTransactionRelationship, { idRelationshipType: T }>;

export type ILegalTransactionRelationship =
  | ILandRegisterQueryRelationship
  | IGeneralRelationship
  | ITestamentRelationship
  | IMarriageRelationship
  | IFamilyRelationship
  | ISignatureCertificationRelationship
  | IInheritanceProvisionRelationship;

export type ILegalTransactionRelationshipSkeleton = Omit<
  ILegalTransactionRelationship,
  'idRelationshipType' | 'data'
>;

export function initLegalTransactionRelationshipSkeleton(): ILegalTransactionRelationshipSkeleton {
  return {
    idRelationship: -random(1, Number.MAX_SAFE_INTEGER),
    RelationshipType: '',
    idLegalTransaction: 0,
    isActiveRelationship: true,
    companyFormationLegalTransaction: null,
    isCompanyFormation: false,
    syncWithArnoTop: false,
    Created: new Date(),
    Updated: new Date(),
    Members: [],
    DocumentArray: [],
    RealEstateArray: [],
    PartnerIdentifiers: [],
    AssetArray: [],
  } as ILegalTransactionRelationshipSkeleton;
}

export function getEmptyRelationshipByType<
  T extends LegalTransactionRelationshipTypeEnum,
>(idRelationshipType: T): RelationshipByType<T> {
  switch (idRelationshipType) {
    case LegalTransactionRelationshipTypeEnum.LandRegisterQuery:
      return getEmptyLandRegisterQueryRelationship() as RelationshipByType<T>;
    case LegalTransactionRelationshipTypeEnum.General:
      return getEmptyGeneralRelationship() as RelationshipByType<T>;
    case LegalTransactionRelationshipTypeEnum.Testament:
      return getEmptyTestamentRelationship() as RelationshipByType<T>;
    case LegalTransactionRelationshipTypeEnum.Family:
      return getEmptyFamilyRelationship() as RelationshipByType<T>;
    case LegalTransactionRelationshipTypeEnum.SignatureCertification:
      return getEmptySignatureCertificationRelationship() as RelationshipByType<T>;
    case LegalTransactionRelationshipTypeEnum.InheritanceProvision:
      return getEmptyInheritanceProvisionRelationship() as RelationshipByType<T>;
    case LegalTransactionRelationshipTypeEnum.Marriage:
      return getEmptyMarriageRelationship() as RelationshipByType<T>;
    default:
      throw new Error('Unsupported relationship type');
  }
}

export function getMembers(relationship: ILegalTransactionRelationship) {
  return relationship.Members as ILegalTransactionRelationshipMember[];
}
